import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogFooter,
  FileUploader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingIcon,
} from '@kalos/ui';
import { type FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { DialogOrSheet } from '../../../../../components/DialogOrSheet';
import { useFileValidation } from '../../../../../hooks/useFileValidation';
import { useMobileDevice } from '../../../../../hooks/useMedia';
import {
  FILE_UPLOAD_ACCEPT,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES,
} from '../../../../../tools/fileUploadUtils';
import { useAddMemoFiles } from './useAddMemoFiles';

const formSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'At least one file is required')
    .max(MAX_FILES, `Cannot upload more than ${MAX_FILES} files`),
});

type FormValues = z.infer<typeof formSchema>;

interface AddMemoFileFormProps {
  memoId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddMemoFileForm: FC<AddMemoFileFormProps> = ({
  memoId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const isMobileDevice = useMobileDevice();
  const { uploadFiles, isSubmitting, isUploadingLargeVideos, isLargeUploading } = useAddMemoFiles({
    memoId,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      files: [],
    },
  });

  const watchedFiles = form.watch('files');

  // Shared real-time file validation
  const { validationError } = useFileValidation(form, 'files', watchedFiles);

  const onSubmit = async (values: FormValues) => {
    await uploadFiles(values.files, () => {
      form.reset();
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    });
  };

  const submitDisabled =
    isSubmitting || isUploadingLargeVideos || isLargeUploading || !!validationError;

  const uploaderDisabled = isSubmitting || isUploadingLargeVideos || isLargeUploading;

  return (
    <DialogOrSheet
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Add Files to Note"
      isBusy={uploaderDisabled}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="files"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Files</FormLabel>
                <FormControl>
                  <FileUploader
                    value={field.value}
                    onValueChange={field.onChange}
                    maxFiles={MAX_FILES}
                    multiple
                    maxSize={MAX_FILE_SIZE_BYTES}
                    accept={FILE_UPLOAD_ACCEPT}
                    disabled={uploaderDisabled}
                    // Phones have no drag and drop: Take Photo and Browse Files instead.
                    useButtons={isMobileDevice}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {isSubmitting || isUploadingLargeVideos || isLargeUploading ? (
                <div className="flex items-center gap-2">
                  <LoadingIcon className="h-4 w-4" />
                  <span>Uploading...</span>
                </div>
              ) : (
                'Upload Files'
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogOrSheet>
  );
};
